/**
 * TypeScript interfaces matching backend Go structs for Client Order
 * Reference: BACKEND/service-nuvertos/libs/utils/client_order/structs/client_order_structs.go
 */

export interface ClientOrder {
  id?: number | string;
  client_id: number | string; // Required
  order_no?: string;
  receive_by_date?: string | null; // ISO 8601 date string
  order_date?: string | null; // ISO 8601 date string
  memo?: string;
  final_amount?: number;
  gross_amount?: number;
  discount_amount?: number;
  tax_amount?: number;
  form_template_id?: number | string;
  location?: string;
  subsidiary_id?: number | string;
  place_of_supply_id?: number | string;
  ship_to_id?: number | string;
  project?: number | string;
  product?: number | string;
  sales_type?: number | string;
  created_by?: number | string;
  updated_by?: number | string;
}

export interface OrderDetails {
  id?: number | string;
  client_order_id?: number | string;
  subsidiary_gst_number?: string;
  customer_gst_number?: string;
  registration_type?: string;
  registration_no?: string;
  tax_details_override?: boolean;
  gst_exemption?: boolean;
  payment_type?: string;
  delivery_date?: string | null; // ISO 8601 date string
  delivery_status?: string;
  terms?: string;
  courier_name?: string;
  shipping_date?: string | null; // ISO 8601 date string
  shipping_carrier?: string;
  shipping_method?: string;
  shipping_address?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_zip?: string;
  shipping_country?: string;
  shipping_phone?: string;
  shipping_email?: string;
  shipping_contact_person?: string;
  shipping_contact_person_phone?: string;
  shipping_contact_person_email?: string;
  billing_address?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  billing_country?: string;
  billing_phone?: string;
  billing_email?: string;
  billing_contact_person?: string;
  billing_contact_person_phone?: string;
  billing_contact_person_email?: string;
  created_by?: number | string;
  created_by_name?: string;
  updated_by?: number | string;
  updated_by_name?: string;
}

export interface OrderItem {
  id?: number | string;
  client_order_id?: number | string;
  item_id: number | string; // Required
  item_name?: string;
  item_type?: string;
  quantity: number; // Required
  foc?: boolean;
  pack?: number;
  scheme?: number;
  description?: string;
  unit_id?: number | string;
  unit_name?: string;
  last_order_qty?: number;
  last_order_price?: number;
  current_stock?: number;
  mrp?: number;
  rate: number; // Required
  amount?: number;
  gross_amount?: number;
  tax_rate?: number;
  tax_type?: string;
  tax_amount?: number;
  net_amount?: number;
  discount_percentage?: number;
  discount_amount?: number;
  total_amount?: number;
  hsn_code?: string;
  uqc?: string;
  line_of_business?: number | string;
  is_deleted?: boolean;
  created_by?: number | string;
  created_by_name?: string;
  updated_by?: number | string;
  updated_by_name?: string;
  created_at?: string;
  updated_at?: string;
  custom_fields?: Record<string, any>;
}

export interface OrderNote {
  id?: number | string;
  title?: string;
  memo?: string;
  date?: string | null; // ISO 8601 date string
  time?: string | null; // ISO 8601 date string
  type?: string;
  direction?: string;
  order_id?: number | string;
  created_by?: number | string;
  created_by_name?: string;
  updated_by?: number | string;
  updated_by_name?: string;
  is_deleted?: boolean;
}

export interface OrderFile {
  id?: number | string;
  file_name: string; // Required
  file: string; // Required
  order_id?: number | string;
  created_by?: number | string;
  created_by_name?: string;
  updated_by?: number | string;
  updated_by_name?: string;
  is_deleted?: boolean;
}

/**
 * Complete backend payload structure for POST /api/v1/client-orders
 */
export interface ClientOrderCreatePayload {
  client_order: ClientOrder; // Required
  order_details?: OrderDetails;
  order_items?: OrderItem[];
  order_notes?: OrderNote[];
  order_files?: OrderFile[];
  custom_fields?: Record<string, any>;
}

/**
 * Complete backend payload structure for PUT /api/v1/client-orders/:id
 */
export interface ClientOrderUpdatePayload {
  client_order?: ClientOrder;
  order_details?: OrderDetails;
  order_items?: OrderItem[];
  order_notes?: OrderNote[];
  order_files?: OrderFile[];
  custom_fields?: Record<string, any>;
}

/**
 * Field to interface mapping for client sales order
 * Maps form field names to their destination in the backend payload structure
 * 
 * Tab Structure:
 * - Tab 1 (Primary Information): header_fields, primary_information -> client_order
 * - Tab 2 (Tax Details): tax_details -> order_details
 * - Tab 3 (Items): items (TABLE) -> order_items
 * - Tab 5 (Shipping & Billing): shipping_details, billing_details -> order_details
 * - Tab 6 (Approval): approval -> client_order (or separate approval object)
 * - Tab 7 (Communications): user_notes (TABLE) -> order_notes
 * - Tab 8 (Audit): audit_logs_filters, audit_logs (read-only, not in payload)
 * - Tab 9 (Workflow and Automations): workflow_and_automation (TABLE, may not be in payload)
 * - Tab 10 (Files): files (TABLE) -> order_files
 */
export const CLIENT_ORDER_FIELD_TO_INTERFACE_MAP: Record<string, string> = {
  // Fields that go to client_order (primary entity)
  // Tab 1: header_fields, primary_information
  client_id: 'client_order',
  order_no: 'client_order',
  receive_by_date: 'client_order',
  order_date: 'client_order',
  memo: 'client_order',
  form_template_id: 'client_order',
  location: 'client_order',
  subsidiary_id: 'client_order',
  place_of_supply_id: 'client_order',
  place_of_supply: 'client_order',
  ship_to_id: 'client_order',
  ship_to: 'client_order', // Maps to ship_to_id in transformPayload
  project: 'client_order',
  product: 'client_order',
  sales_type: 'client_order',
  
  // Fields that go to order_details
  // Tab 2: tax_details
  subsidiary_gst_number: 'order_details',
  customer_gst_number: 'order_details',
  registration_type: 'order_details',
  registration_no: 'order_details',
  tax_details_override: 'order_details',
  gst_exemption: 'order_details',
  payment_type: 'order_details',
  delivery_date: 'order_details',
  delivery_status: 'order_details',
  terms: 'order_details',
  courier_name: 'order_details',
  shipping_date: 'order_details',
  shipping_carrier: 'order_details',
  shipping_method: 'order_details',
  // Tab 5: shipping_details
  shipping_address: 'order_details',
  shipping_city: 'order_details',
  shipping_state: 'order_details',
  shipping_zip: 'order_details',
  shipping_country: 'order_details',
  shipping_phone: 'order_details',
  shipping_email: 'order_details',
  shipping_contact_person: 'order_details',
  shipping_contact_person_phone: 'order_details',
  shipping_contact_person_email: 'order_details',
  // Tab 5: billing_details
  billing_address: 'order_details',
  billing_city: 'order_details',
  billing_state: 'order_details',
  billing_zip: 'order_details',
  billing_country: 'order_details',
  billing_phone: 'order_details',
  billing_email: 'order_details',
  billing_contact_person: 'order_details',
  billing_contact_person_phone: 'order_details',
  billing_contact_person_email: 'order_details',
  bill_to: 'order_details', // Maps to billing_address in transformPayload
};

/**
 * Table to interface mapping for client sales order
 * Maps UI table keys (subTab.key) to backend array properties
 * 
 * Tab Structure:
 * - Tab 3: items (subTab.key: 'items') -> order_items
 * - Tab 7: user_notes (subTab.key: 'user_notes') -> order_notes
 * - Tab 10: files (subTab.key: 'files') -> order_files
 * - Tab 9: workflow_and_automation (subTab.key: 'workflow_and_automation') - may not be in payload
 * - Tab 8: audit_logs (subTab.key: 'audit_logs') - read-only, not in payload
 */
export const CLIENT_ORDER_TABLE_TO_INTERFACE_MAP: Record<string, string> = {
  'items': 'order_items',
  'user_notes': 'order_notes',
  'files': 'order_files',
  // Note: workflow_and_automation and audit_logs are not included as they may not be part of create/update payload
};

